import { useToast } from "@/components/ui/use-toast";
import { mintActivityUploadUrl } from "@/lib/api/activities";
import { type ConnectLibraryAnswer } from "@/lib/api/connect-library";
import { useAudioPlayback } from "@/lib/hooks/use-audio-playback";
import { useAudioRecorder } from "@/lib/hooks/use-audio-recorder";
import { useNotificationMuteActivity } from "@/lib/hooks/conversations/use-notification-mute-activity";
import { useAudioTranscoder } from "@/lib/hooks/use-audio-transcoder";
import {
  useAudioBlob,
  useLiveAudioWaveform,
  useStaticAudioWaveform,
} from "@/lib/hooks/use-audio-waveform";
import { usePostHogEvent } from "@/lib/posthog";
import { useCallback, useEffect, useMemo, useState } from "react";
import type { RecorderUiState } from "./row-audio-recorder";

const MAX_DURATION_MS = 60_000;

type UploadStage = "idle" | "transcoding" | "uploading" | "saving";

export interface ConfirmedResponderAnswer {
  audioS3Key: string;
  durationMs: number;
  fromLibrary: boolean;
}

interface UseConnectResponderRowOptions {
  entityId: string;
  index: 0 | 1 | 2;
  questionId: string;
  libraryAnswer: ConnectLibraryAnswer | null;
  micDeviceId: string | null;
  onRecordingStateChange: (isRecording: boolean) => void;
  onConfirmedChange: (confirmed: ConfirmedResponderAnswer | null) => void;
}

export interface ConnectResponderRow {
  uiState: RecorderUiState;
  confirmed: ConfirmedResponderAnswer | null;
  hasUnconfirmedLibraryAnswer: boolean;
  isConfirmingLibraryAnswer: boolean;
  start: () => void;
  stop: () => void;
  play: () => void;
  pause: () => void;
  reRecord: () => void;
  clear: () => void;
  confirmLibraryAnswer: () => void;
  retryPermission: () => void;
}

export function useConnectResponderRow({
  entityId,
  index,
  questionId,
  libraryAnswer,
  micDeviceId,
  onRecordingStateChange,
  onConfirmedChange,
}: UseConnectResponderRowOptions): ConnectResponderRow {
  const { toast } = useToast();
  const recorder = useAudioRecorder({
    maxDurationMs: MAX_DURATION_MS,
    deviceId: micDeviceId,
  });
  const transcoder = useAudioTranscoder();
  const { dispatchEvent } = usePostHogEvent("activity.mentor");

  const [uploadStage, setUploadStage] = useState<UploadStage>("idle");
  const [confirmed, setConfirmed] = useState<ConfirmedResponderAnswer | null>(
    null,
  );
  const [localCompletedBlob, setLocalCompletedBlob] = useState<Blob | null>(
    null,
  );

  const updateConfirmed = useCallback(
    (value: ConfirmedResponderAnswer | null) => {
      setConfirmed(value);
      onConfirmedChange(value);
    },
    [onConfirmedChange],
  );

  const liveBars = useLiveAudioWaveform(recorder.stream);

  const libraryUrlForBlob = libraryAnswer?.audioUrl ?? null;
  const libraryBlob = useAudioBlob(
    localCompletedBlob ? null : libraryUrlForBlob,
  );

  const playbackBlob = useMemo(() => {
    if (recorder.state === "stopped" && recorder.result) {
      return recorder.result.blob;
    }
    if (localCompletedBlob) return localCompletedBlob;
    return libraryBlob;
  }, [recorder.state, recorder.result, localCompletedBlob, libraryBlob]);

  const staticBars = useStaticAudioWaveform(playbackBlob);

  useEffect(() => {
    onRecordingStateChange(recorder.state === "recording");
  }, [recorder.state, onRecordingStateChange]);

  const playbackUrl = useMemo(() => {
    if (localCompletedBlob) return URL.createObjectURL(localCompletedBlob);
    return libraryAnswer?.audioUrl ?? null;
  }, [localCompletedBlob, libraryAnswer?.audioUrl]);

  useEffect(() => {
    if (!playbackUrl?.startsWith("blob:")) return;
    return () => URL.revokeObjectURL(playbackUrl);
  }, [playbackUrl]);

  const { isPlaying, progressMs, play, pause } = useAudioPlayback(playbackUrl);

  useNotificationMuteActivity(
    recorder.state === "recording" || recorder.state === "starting",
    "connect-responder-recorder",
  );
  useNotificationMuteActivity(isPlaying, "connect-responder-playback");

  const uploadToActivity = useCallback(
    async (m4a: Blob): Promise<string> => {
      const presigned = await mintActivityUploadUrl(
        entityId,
        `response_level_${index}`,
        "audio/m4a",
      );
      const putResponse = await fetch(presigned.presignedUrl, {
        method: "PUT",
        headers: { "Content-Type": presigned.contentType },
        body: m4a,
      });
      if (!putResponse.ok) {
        throw new Error(`S3 upload failed (${putResponse.status})`);
      }
      return presigned.s3Key;
    },
    [entityId, index],
  );

  const runRecordingUpload = useCallback(
    async (sourceBlob: Blob, durationMs: number) => {
      try {
        setUploadStage("transcoding");
        const m4a = await transcoder.transcodeToM4a(sourceBlob);

        setUploadStage("uploading");
        const audioS3Key = await uploadToActivity(m4a);

        updateConfirmed({ audioS3Key, durationMs, fromLibrary: false });
        setLocalCompletedBlob(m4a);
        dispatchEvent("mentor_chat_connect_response_record_end", {
          entityId,
          questionIndex: index,
          questionId,
          durationSeconds: Math.round(durationMs / 1000),
        });
      } catch (err) {
        console.error("[connect-responder-row] upload pipeline failed", err);
        toast({
          title: "Couldn't save your recording",
          description:
            err instanceof Error && err.message
              ? err.message
              : "Something went wrong. Try again.",
          variant: "destructive",
        });
        recorder.reset();
        setLocalCompletedBlob(null);
        updateConfirmed(null);
      } finally {
        setUploadStage("idle");
      }
    },
    [
      transcoder,
      uploadToActivity,
      dispatchEvent,
      entityId,
      index,
      questionId,
      toast,
      recorder,
      updateConfirmed,
    ],
  );

  useEffect(() => {
    if (
      recorder.state === "stopped" &&
      recorder.result &&
      uploadStage === "idle" &&
      !confirmed
    ) {
      const { blob, durationMs } = recorder.result;
      void runRecordingUpload(blob, durationMs);
    }
    // Excluding runRecordingUpload prevents re-fire if its identity changes mid-upload.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recorder.state, recorder.result]);

  const [isConfirmingLibraryAnswer, setIsConfirmingLibraryAnswer] =
    useState(false);

  const confirmLibraryAnswer = useCallback(() => {
    if (!libraryAnswer || !libraryBlob || confirmed) return;
    setIsConfirmingLibraryAnswer(true);
    const run = async () => {
      setUploadStage("uploading");
      const audioS3Key = await uploadToActivity(libraryBlob);
      updateConfirmed({
        audioS3Key,
        durationMs: libraryAnswer.durationMs,
        fromLibrary: true,
      });
      dispatchEvent("mentor_chat_connect_response_library_confirmed", {
        entityId,
        questionIndex: index,
        questionId,
      });
    };
    void run()
      .catch((err) => {
        console.error("[connect-responder-row] library confirm failed", err);
        toast({
          title: "Couldn't use this answer",
          description: "Something went wrong. Try again.",
          variant: "destructive",
        });
      })
      .finally(() => {
        setUploadStage("idle");
        setIsConfirmingLibraryAnswer(false);
      });
  }, [
    libraryAnswer,
    libraryBlob,
    confirmed,
    uploadToActivity,
    dispatchEvent,
    entityId,
    index,
    questionId,
    toast,
    updateConfirmed,
  ]);

  const start = useCallback(() => {
    dispatchEvent("mentor_chat_connect_response_record_start", {
      entityId,
      questionIndex: index,
      questionId,
      isRerecord: confirmed !== null || !!libraryAnswer,
    });
    void recorder.start();
  }, [
    recorder,
    dispatchEvent,
    entityId,
    index,
    questionId,
    confirmed,
    libraryAnswer,
  ]);

  const stop = useCallback(() => {
    recorder.stop();
  }, [recorder]);

  const reRecord = useCallback(() => {
    setLocalCompletedBlob(null);
    updateConfirmed(null);
    recorder.reset();
    void recorder.start();
  }, [recorder, updateConfirmed]);

  const clear = useCallback(() => {
    setLocalCompletedBlob(null);
    updateConfirmed(null);
    recorder.reset();
  }, [recorder, updateConfirmed]);

  const hasUnconfirmedLibraryAnswer = !!libraryAnswer && !confirmed;

  const uiState = useMemo<RecorderUiState>(() => {
    if (recorder.error?.kind === "permission_denied") {
      return { kind: "permission_denied" };
    }
    if (recorder.error?.kind === "unsupported") {
      return { kind: "unsupported" };
    }
    if (recorder.state === "starting") {
      return { kind: "starting" };
    }
    if (recorder.state === "recording") {
      return {
        kind: "recording",
        durationMs: recorder.durationMs,
        bars: liveBars,
      };
    }
    if (uploadStage !== "idle") {
      return {
        kind: "uploading",
        durationMs: recorder.durationMs,
        stage: uploadStage === "saving" ? "uploading" : uploadStage,
      };
    }
    if (confirmed || libraryAnswer) {
      return {
        kind: "completed",
        durationMs:
          confirmed?.durationMs ??
          recorder.result?.durationMs ??
          libraryAnswer?.durationMs ??
          0,
        bars: staticBars,
        isPlaying,
        progressMs,
      };
    }
    return { kind: "default" };
  }, [
    recorder.error,
    recorder.state,
    recorder.durationMs,
    recorder.result,
    liveBars,
    uploadStage,
    confirmed,
    libraryAnswer,
    staticBars,
    isPlaying,
    progressMs,
  ]);

  return {
    uiState,
    confirmed,
    hasUnconfirmedLibraryAnswer,
    isConfirmingLibraryAnswer,
    start,
    stop,
    play,
    pause,
    reRecord,
    clear,
    confirmLibraryAnswer,
    retryPermission: start,
  };
}
